import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import SurveyTemplate from "./SurveyTemplate";
import "../styles/components/MultiPageSurvey.css";
import starsIcon from "../assets/icons/stars.svg";

const SurveyPage1 = ({
  surveyData,
  updateSurveyData,
  onNext,
  onBack,
  onNavigationBack,
  currentSection,
  initialQuestionIndex = 0,
  updatePageState,
  pageNumber,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState(initialQuestionIndex);
  const [localData, setLocalData] = useState({
    biologicalSex: surveyData.biologicalSex || "",
    ethnicity: surveyData.ethnicity || "",
    age: surveyData.age || "",
    location: surveyData.location || "",
  });
  const [ageError, setAgeError] = useState("");
  const MOBILE_INFO_THRESHOLD = 780;
  const [showMobileInfo, setShowMobileInfo] = useState(() => {
    if (typeof window === "undefined") return false;
    const w = window.innerWidth;
    const show = w > MOBILE_INFO_THRESHOLD;
    console.log("[WhyThisMatters] SurveyPage1 mobile panel initial:", { width: w, threshold: MOBILE_INFO_THRESHOLD, show });
    return show;
  });
  const updatePageStateRef = useRef(updatePageState);
  updatePageStateRef.current = updatePageState;
  const lastSyncedQuestion = useRef(null);

  useEffect(() => {
    const check = () => {
      const w = window.innerWidth;
      const show = w > MOBILE_INFO_THRESHOLD;
      setShowMobileInfo(show);
      console.log("[WhyThisMatters] SurveyPage1 mobile panel resize:", { width: w, threshold: MOBILE_INFO_THRESHOLD, show });
    };
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Sync to parent only when currentQuestion changes (skip if already synced this value to avoid loop)
  useEffect(() => {
    if (lastSyncedQuestion.current === currentQuestion || !updatePageStateRef.current || !pageNumber) return;
    lastSyncedQuestion.current = currentQuestion;
    updatePageStateRef.current(pageNumber, currentQuestion);
  }, [currentQuestion, pageNumber]);

  const questions = [
    {
      id: "biologicalSex",
      question: t("survey.page1.biologicalSex.question"),
      intro: t("survey.page1.biologicalSex.intro", { defaultValue: t("survey.page1.intro") }),
      options: [
        { value: "male", label: t("survey.page1.biologicalSex.options.male") },
        {
          value: "female",
          label: t("survey.page1.biologicalSex.options.female"),
        },
        {
          value: "other",
          label: t("survey.page1.biologicalSex.options.other"),
        },
      ],
      info: t("survey.page1.biologicalSex.info"),
    },
    {
      id: "ethnicity",
      question: t("survey.page1.ethnicity.question"),
      intro: t("survey.page1.ethnicity.intro", { defaultValue: t("survey.page1.intro") }),
      options: [
        { value: "asian", label: t("survey.page1.ethnicity.options.asian") },
        {
          value: "white",
          label: t("survey.page1.ethnicity.options.white"),
        },
        {
          value: "black",
          label: t("survey.page1.ethnicity.options.black"),
        },
        {
          value: "hispanic",
          label: t("survey.page1.ethnicity.options.hispanic"),
        },
        {
          value: "native_american",
          label: t("survey.page1.ethnicity.options.native_american"),
        },
        {
          value: "pacific_islander",
          label: t("survey.page1.ethnicity.options.pacific_islander"),
        },
        {
          value: "middle_eastern",
          label: t("survey.page1.ethnicity.options.middle_eastern"),
        },
        { value: "mixed", label: t("survey.page1.ethnicity.options.mixed") },
        { value: "prefer_not_to_say", label: t("survey.page1.ethnicity.options.prefer_not_to_say") },
      ],
      info: t("survey.page1.ethnicity.info"),
    },
    {
      id: "age",
      question: t("survey.page1.age.question"),
      intro: t("survey.page1.age.intro", { defaultValue: t("survey.page1.intro") }),
      type: "number",
      placeholder: t("survey.page1.age.placeholder"),
      info: t("survey.page1.age.info"),
    },
    // Location question hidden for now
    // {
    //   id: "location",
    //   question: t('survey.page1.location.question'),
    //   options: [
    //     { value: "china", label: t('survey.page1.location.options.china') },
    //     { value: "united_states", label: t('survey.page1.location.options.united_states') },
    //     { value: "australia", label: t('survey.page1.location.options.australia') },
    //     { value: "singapore", label: t('survey.page1.location.options.singapore') },
    //     { value: "other", label: t('survey.page1.location.options.other') },
    //   ],
    //   info: t('survey.page1.location.info'),
    // },
  ];

  const handleOptionSelect = (value) => {
    const currentQ = questions[currentQuestion];
    setLocalData((prev) => ({
      ...prev,
      [currentQ.id]: value,
    }));

    // Auto-advance for single-select questions after a brief delay
    if (currentQ.type !== "number" && currentQ.type !== "multiSelect") {
      setTimeout(() => {
        handleNext();
      }, 300);
    }
  };

  const handleInputChange = (e) => {
    const currentQ = questions[currentQuestion];
    const value = e.target.value;

    setLocalData((prev) => ({
      ...prev,
      [currentQ.id]: value,
    }));

    // Validate age input
    if (currentQ.id === "age") {
      validateAge(value);
    }
  };

  const validateAge = (ageValue) => {
    const age = parseInt(ageValue, 10);

    if (ageValue === "" || ageValue === "0") {
      setAgeError("");
      return true;
    }

    if (isNaN(age) || age < 1 || age > 120) {
      setAgeError("Please enter a valid age between 1 and 120 years");
      return false;
    }

    setAgeError("");
    return true;
  };

  const handleNext = () => {
    // Validate current question before proceeding
    const currentQ = questions[currentQuestion];
    if (currentQ.id === "age") {
      const isValidAge = validateAge(localData.age);
      if (!isValidAge) {
        return; // Don't proceed if age is invalid
      }
    }

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Save all data and move to next page
      Object.keys(localData).forEach((key) => {
        let value = localData[key];

        // Convert age to number if it's the age field
        if (key === "age" && value !== "") {
          value = parseInt(value, 10);
        }

        updateSurveyData(key, value);
      });

      // Move to next page
      onNext();
    }
  };

  const handleHeaderBack = () => {
    // Header back button goes to landing page for SurveyPage1
    navigate("/landing");
  };

  const handleNavigationBack = () => {
    // Navigation back button goes to previous question within the page
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    } else {
      // If on first question, go to previous page
      if (onNavigationBack) {
        onNavigationBack();
      }
    }
  };

  const currentQ = questions[currentQuestion];
  const isAnswered =
    localData[currentQ.id] !== undefined &&
    localData[currentQ.id] !== null &&
    (currentQ.type === "number"
      ? localData[currentQ.id] !== "" && localData[currentQ.id] !== 0
      : localData[currentQ.id].toString().trim() !== "");
  const isLastQuestion = currentQuestion === questions.length - 1;
  const hasError = currentQ.id === "age" && ageError !== "";

  const surveyConfig = {
    introText: currentQ.intro || t("survey.page1.intro"),
    questionText: currentQ.question,
    options: currentQ.type === "number" ? [] : currentQ.options || [],
    infoSections: [
      {
        title: t("survey.page1.whyThisMatters"),
        content: currentQ.info,
      },
      {
        title: t("survey.page1.yourPrivacy"),
        content: t("survey.page1.privacyText"),
      },
    ],
  };

  return (
    <div className="survey-page-1">
      <SurveyTemplate
        introText={surveyConfig.introText}
        questionText={surveyConfig.questionText}
        options={surveyConfig.options}
        infoSections={surveyConfig.infoSections}
        onOptionSelect={handleOptionSelect}
        selectedOption={localData[currentQ.id]}
        onBack={handleHeaderBack}
        consentText={currentQuestion === 0 ? t("survey.consent") : undefined}
        infoPanelLayout={currentQ.id === "biologicalSex" ? "collapsible" : "sidepanel"}
        customInput={
          currentQ.type === "number" ? (
            <div className="custom-input-wrapper">
              <input
                type="number"
                value={localData[currentQ.id]}
                onChange={handleInputChange}
                placeholder={currentQ.placeholder}
                className={`option-input ${hasError ? "error" : ""}`}
                min="1"
                max="120"
              />
              {hasError && <div className="error-message">{ageError}</div>}
            </div>
          ) : null
        }
        progressContainer={
          <div className="progress-container">
            {/* Section Progress */}
            <div className="section-progress">
              <div className="section-indicators">
                <div
                  className={`section-indicator ${
                    currentSection >= 1 ? "active" : ""
                  }`}
                >
                  <span className="section-number">1</span>
                  <span className="section-label">
                    {t("survey.sections.demographics")}
                  </span>
                </div>
                <div
                  className={`section-indicator ${
                    currentSection >= 2 ? "active" : ""
                  }`}
                >
                  <span className="section-number">2</span>
                  <span className="section-label">
                    {t("survey.sections.physical")}
                  </span>
                </div>
                <div
                  className={`section-indicator ${
                    currentSection >= 3 ? "active" : ""
                  }`}
                >
                  <span className="section-number">3</span>
                  <span className="section-label">
                    {t("survey.sections.family")}
                  </span>
                </div>
                <div
                  className={`section-indicator ${
                    currentSection >= 4 ? "active" : ""
                  }`}
                >
                  <span className="section-number">4</span>
                  <span className="section-label">
                    {t("survey.sections.lifestyle")}
                  </span>
                </div>
                <div
                  className={`section-indicator ${
                    currentSection >= 5 ? "active" : ""
                  }`}
                >
                  <span className="section-number">5</span>
                  <span className="section-label">
                    {t("survey.sections.symptoms")}
                  </span>
                </div>
              </div>
            </div>

            {/* Question Progress */}
            <div className="question-progress">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{
                    width: `${
                      ((currentQuestion + 1) / questions.length) * 100
                    }%`,
                    height: `${
                      ((currentQuestion + 1) / questions.length) * 100
                    }%`,
                  }}
                ></div>
              </div>
              <span className="progress-text">
                {currentQuestion + 1} of {questions.length}
              </span>
            </div>
          </div>
        }
        navigationButtons={
          <div className="navigation-buttons">
            <button
              className="nav-button back-button"
              onClick={handleNavigationBack}
              disabled={currentQuestion === 0}
            >
              {t("common.back")}
            </button>
            <button
              className="nav-button next-button"
              onClick={handleNext}
              disabled={!isAnswered || hasError}
            >
              {isLastQuestion ? t('common.continue') : t('common.next')}
            </button>
          </div>
        }
      />

      {/* Info Panel - hidden below 780px */}
      {showMobileInfo && (
        <div className="mobile-info-panel">
          <div className="info-section">
            <div className="info-header">
              <img src={starsIcon} alt="Stars" className="star-icon" />
              <h3>{t("survey.whyWeAsk")}</h3>
            </div>
            <p className="info-text">{questions[currentQuestion].info}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SurveyPage1;
